import { CommonModule, DatePipe } from '@angular/common';
import { Component, effect, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ExportService } from '../../../services/export/export.service';
import { SpecificClaimsListService } from '../../../services/view/specific-claims-list.service';
import { WatchlistService } from '../../../services/view/watchlist.service';
import { ClaimExportMapper } from '../../../utilities/claim-export-mapper';
import { BaseComponent } from '../../base/base.component';
import { ExportFormat } from '../../core/buttons/export-button.component';
import { ApiErrorLabelComponent } from '../../core/labels/api-error-label.component';
import { EmptyStateComponent } from '../../core/panels/empty-state.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../core/panels/loading-panel.component';
import { ClaimViewComponent } from '../claim-view/claim-view.component';
import { Snackbar } from '../../../services/snackbar/snackbar.service';

@Component({
  selector: 'app-claim-list',
  imports: [
    CommonModule,
    LoadingPanelComponent,
    RouterModule,
    ClaimViewComponent,
    EmptyStateComponent,
    ApiErrorLabelComponent,
  ],
  template: `
    <app-loading-panel
      [state]="state"
      loadingStyle="Skeleton"
      [loadingHeight]="450"
    >
      <app-api-error-label [error]="error" />

      <ng-template #empty>
        <app-empty-state
          icon="fa-light fa-list"
          label="Empty claim list"
          height="450px"
        >
        </app-empty-state>
      </ng-template>

      <ng-template #loaded>
        <p class="tw-text-sm tw-italic tw-text-gray-600 tw-mt-4">
          Access to each claim is effective for a specific time period. Claims
          will be removed from the list once the expiration date has been
          reached.
        </p>
        <app-claim-view
          [claimView]="specificClaimsList.claims()"
          [columnView]="specificClaimsList.claims()"
          [showSelection]="false"
          [showRemove]="false"
          [showDateAccessed]="true"
          [claimViewAriaLabel]="'Claim list'"
          [showWatchButton]="true"
          sortField="dateAccessed"
          [sortOrder]="-1"
          (export)="onExport($event)"
          (columnsUpdated)="onColumnsUpdated()"
        />
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class ClaimListComponent extends BaseComponent implements OnInit {
  specificClaimsList = inject(SpecificClaimsListService);
  exportService = inject(ExportService);
  private watchlist = inject(WatchlistService);
  private datePipe = inject(DatePipe);
  private snackbar = inject(Snackbar);

  protected state: LoadingState = 'Loading';
  error: any = null;

  constructor() {
    super();

    effect(() => {
      const claims = this.specificClaimsList.claims();
      if (!claims) {
        this.state = 'Loading';
      } else if (claims.rows.length > 0) {
        this.state = 'Loaded';
      } else {
        this.state = 'Empty';
      }
    });
  }

  async ngOnInit(): Promise<void> {
    await this.loadClaims();
  }

  private async loadClaims(): Promise<void> {
    try {
      this.state = 'Loading';
      this.error = null;
      await this.specificClaimsList.load();
    } catch (error) {
      this.error = error;
      this.state = 'Empty';
    }
  }

  async onExport(format: ExportFormat): Promise<void> {
    try {
      const claims = this.specificClaimsList.claims();
      const watchedClaims = this.watchlist.claims()?.rows ?? [];
      const rows = claims.rows.map((row) => ({
        ...row,
        watchStatus: watchedClaims.some(
          (w) =>
            w.recordId === row.recordId && w.recordSource === row.recordSource,
        )
          ? 'Y'
          : 'N',
      }));

      const claimColumns = claims.columns;
      const columns = [
        {
          propertyName: 'watchStatus',
          headerText: 'Watchlist',
        },
        ...claimColumns,
      ];

      const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
      const filename = `SpecificClaims_${formattedDate}.${format}`;

      if (format === 'csv') {
        const csvData = ClaimExportMapper.toCsvExport(
          rows,
          columns,
          'Specific Claims',
        );
        await this.exportService.exportCsv(csvData, filename);
      } else if (format === 'xlsx') {
        const excelData = ClaimExportMapper.toExcelExport(
          rows,
          columns,
          'Specific Claims',
          'OmniInsights Export',
        );
        await this.exportService.exportExcel(excelData, filename);
      }
    } catch (error) {
      this.error = error;
      this.snackbar.show('An error occurred while exporting claims.', 'Error');
    }
  }

  async onColumnsUpdated(): Promise<void> {
    this.state = 'Loading';
    await this.specificClaimsList.forceLoad();
    const data = this.specificClaimsList.claims();
    this.state = data?.rows.length ? 'Loaded' : 'Empty';
  }
}
